import { inject, Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpHeaders,
  HttpClient
} from '@angular/common/http';
import { catchError, from, lastValueFrom, Observable, switchMap, throwError } from 'rxjs';
import { environment } from './environments/environment';
import { NotificationService } from './app/services/notification.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private readonly HEADERS = {
    CONTENT_TYPE_FORM: 'application/x-www-form-urlencoded; charset=UTF-8',
    ACCEPT_JSON: 'application/json'
  };
  // private readonly SESSION_TIMEOUT_MS = 2 * 60 * 1000;
  private readonly SESSION_TIMEOUT_MS = 4 * 60 * 60 * 1000;
  private isLogoutInProgress = false;
  private readonly notificationService = inject(NotificationService);

  private isSessionExpiredFromLastModifiedOn(): boolean {
    const lastModifiedOn = sessionStorage.getItem('lastModifiedOn');
    if (!lastModifiedOn) {
      return true;
    }

    const lastModifiedMs = Number.parseInt(lastModifiedOn, 10);
    if (Number.isNaN(lastModifiedMs)) {
      return true;
    }

    return Date.now() - lastModifiedMs >= this.SESSION_TIMEOUT_MS;
  }

  currentUserAccessTkn: any;
  currentUserIDTkn: any
  https = inject(HttpClient);
  idToken: any;
  accessToken: any;
  refresToken: any;
  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const idToken = sessionStorage.getItem('id_token');
    const isAuthFlow = globalThis.location.href.includes('code=') || globalThis.location.href.includes('error=');

    if (req.headers.has('Authorization')) {
      return next.handle(req);
    }

    if (isAuthFlow) {
      return next.handle(req);
    }

    if (!idToken) {
      return next.handle(req);
    }

    if (this.isSessionExpiredFromLastModifiedOn()) {
      this.clearData();
      return next.handle(req);
    }

    if (this.isTokenExpired(idToken)) {
      this.redirectToAzureLogin();
      return next.handle(req);
    }

    const clonedRequest = req.clone({
      headers: req.headers.set('Authorization', `Bearer ${idToken}`)
    });

    return next.handle(clonedRequest).pipe(
      catchError((error) => {
        console.trace('HTTP error occurred:', error);

        if (error.status === 401) {
          console.trace('error401', error);
          return from(this.refreshToken()).pipe(
            switchMap(() => {
              const newToken = sessionStorage.getItem('id_token');
              const retryRequest = req.clone({
                headers: req.headers.set('Authorization', `Bearer ${newToken}`)
              });
              return next.handle(retryRequest);
            }),
            catchError((refreshError) => {
              console.trace('Token refresh failed, redirecting to login:', refreshError);
              this.redirectToAzureLogin();
              return throwError(() => refreshError);
            })
          );
        }
        return throwError(() => error);
      })
    );
  }

  private isTokenExpired(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (!payload.exp) {
        return true;
      }

      const expirationTime = payload.exp * 1000;
      const currentTime = Date.now();

      return currentTime >= expirationTime;
    } catch (error) {
      console.trace('Error decoding token:', error);
      return true;
    }
  }

  async refreshToken() {
    const refreshToken = sessionStorage.getItem('Refresh_token');
    const accessToken = sessionStorage.getItem('token');
    const idToken = sessionStorage.getItem('id_token');

    const isValidToken =
      refreshToken !== 'undefined' && refreshToken !== '' &&
      accessToken !== 'undefined' && accessToken !== '' &&
      idToken !== 'undefined' && idToken !== '';

    if (!isValidToken) {
      this.clearData();
      return;
    }

    const data: any = {
      grant_type: "refresh_token",
      client_id: environment.clientId,
      refresh_token: refreshToken
    };

    const refreshBody: string[] = [];
    for (const property in data) {
      refreshBody.push(
        `${encodeURIComponent(property)}=${encodeURIComponent(data[property])}`
      );
    }

    const body = refreshBody.join("&");

    const isIEOrEdge = /msie\s|trident\/|edge\//i.test(globalThis.navigator.userAgent);

    const headerObj: any = {
      'Content-Type': this.HEADERS.CONTENT_TYPE_FORM,
      'Accept': this.HEADERS.ACCEPT_JSON,
      'Origin': globalThis.location.origin
    };

    if (isIEOrEdge) {
      headerObj['Cache-Control'] = 'no-cache';
      headerObj['Pragma'] = 'no-cache';
    }

    const headers = new HttpHeaders(headerObj);

    try {
      const res: any = await lastValueFrom(
        this.https.post(environment.tokenURL, body, { headers })
      );

      const isSuccess =
        res !== null &&
        res !== undefined &&
        res !== '' &&
        !res.hasOwnProperty('error');

      if (isSuccess) {
        console.info('[AppComponent] Token refreshed successfully');

        this.idToken = res.id_token;
        this.accessToken = res.access_token;
        this.refresToken = res.refresh_token;

        sessionStorage.setItem('token', this.accessToken);
        sessionStorage.setItem('Refresh_token', this.refresToken);
        sessionStorage.setItem('id_token', this.idToken);
        return;
      }

      console.trace('[AppComponent] Token refresh failed:', res?.error, res?.error_description);

      const errorCode = res?.error || '';

      if (errorCode.includes('AADSTS70008') || errorCode.includes('invalid_grant')) {
        console.info('[AppComponent] Refresh token expired, clearing session');
        this.clearData();
      }

    } catch (error: any) {
      console.trace('[AppComponent] HTTP error during token refresh:', error);
      this.clearData();
    }
  }

  clearData() {
    if (this.isLogoutInProgress) {
      return;
    }

    this.isLogoutInProgress = true;
    this.notificationService.showTokenExpired(3000);

    setTimeout(() => {
      sessionStorage.clear();
      const postLogoutUri = encodeURIComponent(environment.redirectUri);
      globalThis.location.href = `${environment.logoutUrl}?post_logout_redirect_uri=${postLogoutUri}`;
    }, 3000);
  }

  private redirectToAzureLogin(): void {
    globalThis.location.href = '/';
  }
}